import { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Copy,
  FileText,
  Calendar,
  Cloud,
  CloudOff,
  Clock,
  Loader2,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Input } from '@/app/components/ui/input';
import {
  useDailyReports,
  useCreateDailyReport,
  useUpdateDailyReport,
  useAddCrewEntry,
  useAddDelayEntry,
  useUploadPhoto,
} from '@/hooks/useDailyReports';
import { dataUrlToFile } from './create-daily-report-page';
import {
  getSubmitQueue,
  useOnlineSync,
  type QueuedSubmission,
} from './use-offline-draft';
import type { DailyReport, DelayType } from '@/lib/types';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import { useOrgTimeZone, useOrgToday } from '@/hooks/use-settings';

export function DailyReportsListPage() {
  const orgToday = useOrgToday();
  const timeZone = useOrgTimeZone();
  const { id } = useParams<{ id: string }>();
  const projectId = Number(id);
  const navigate = useNavigate();

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data, isLoading } = useDailyReports({
    project: projectId,
    page_size: 50,
    ordering: '-date',
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });

  const reports = data?.results ?? [];
  const queuedItems = getSubmitQueue().filter(
    (q) => q.projectId === projectId
  );

  // Offline sync
  const createReport = useCreateDailyReport();
  const updateReport = useUpdateDailyReport();
  const addCrew = useAddCrewEntry();
  const addDelay = useAddDelayEntry();
  const uploadPhoto = useUploadPhoto();

  const handleFlush = useCallback(
    async (item: QueuedSubmission) => {
      const report = await createReport.mutateAsync(
        item.payload as unknown as Parameters<typeof createReport.mutateAsync>[0]
      );
      for (const crew of item.crewPayloads) {
        const payload = crew.person
          ? {
              daily_report: report.id,
              person: crew.person,
              role: crew.role,
              hours_on_site: crew.hours_on_site || null,
            }
          : {
              daily_report: report.id,
              tech: crew.tech,
              role: crew.role,
              hours_on_site: crew.hours_on_site || null,
            };
        await addCrew.mutateAsync(payload as unknown as Parameters<typeof addCrew.mutateAsync>[0]);
      }
      for (const delay of item.delayPayloads) {
        const createdDelay = await addDelay.mutateAsync({
          daily_report: report.id,
          delay_type: delay.delay_type as DelayType,
          start_time: delay.start_time || null,
          end_time: delay.end_time || null,
          billable: delay.billable,
          client_approval_required: delay.client_approval_required ?? false,
          description: delay.description,
          rate_override: delay.billable ? (delay.rate_override || null) : null,
        });
        // Upload delay-attached photos
        if (delay.photoFiles?.length) {
          for (let pi = 0; pi < delay.photoFiles.length; pi++) {
            const file = dataUrlToFile(delay.photoFiles[pi], `delay_photo_${pi}.jpg`);
            await uploadPhoto.mutateAsync({
              dailyReportId: report.id,
              file,
              caption: delay.photoCaptions[pi] ?? '',
              delayEntryId: createdDelay.id,
            });
          }
        }
      }
      // Upload queued photos
      for (let i = 0; i < item.photoDataUrls.length; i++) {
        const file = dataUrlToFile(item.photoDataUrls[i], `photo_${i}.jpg`);
        await uploadPhoto.mutateAsync({
          dailyReportId: report.id,
          file,
          caption: item.photoCaptions[i] ?? '',
        });
      }
      // Mark as submitted
      await updateReport.mutateAsync({
        id: report.id,
        data: { submitted_at: new Date().toISOString() },
      });
    },
    [createReport, updateReport, addCrew, addDelay, uploadPhoto]
  );

  useOnlineSync(handleFlush);

  // Copy yesterday as today
  const [copying, setCopying] = useState(false);
  const handleCopyYesterday = async () => {
    if (reports.length === 0) return;
    const latest = reports[0];
    setCopying(true);
    try {
      const report = await createReport.mutateAsync({
        project: projectId,
        date: orgToday,
        weather: latest.weather,
        shift_start: latest.shift_start,
        shift_end: latest.shift_end,
        productive_hours: latest.productive_hours,
        notes: '',
        supervisor: latest.supervisor,
      });
      // Copy crew
      for (const crew of latest.crew_entries) {
        await addCrew.mutateAsync({
          daily_report: report.id,
          person: crew.person ?? crew.tech,
          role: crew.role,
          hours_on_site: crew.hours_on_site,
        });
      }
      navigate(`/work/daily-reports/${projectId}/${report.id}`);
    } catch {
      // show in UI — user can retry
    } finally {
      setCopying(false);
    }
  };

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)]">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b px-4 py-3 space-y-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/work/daily-reports`)}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold flex-1">Daily Reports</h1>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            className="flex-1 h-11"
            onClick={() =>
              navigate(`/work/daily-reports/${projectId}/new`)
            }
          >
            <Plus className="h-4 w-4 mr-1" /> New Report
          </Button>
          {reports.length > 0 && (
            <Button
              variant="outline"
              className="h-11"
              onClick={handleCopyYesterday}
              disabled={copying}
            >
              {copying ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-1" /> Copy Last
                </>
              )}
            </Button>
          )}
        </div>

        {/* Date filters */}
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="date"
            placeholder="From"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="h-9 text-sm"
          />
          <Input
            type="date"
            placeholder="To"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="h-9 text-sm"
          />
        </div>
      </div>

      {/* Queued items banner */}
      {queuedItems.length > 0 && (
        <div className="mx-4 mt-3 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm text-amber-700 flex items-center gap-2">
          <CloudOff className="h-4 w-4 shrink-0" />
          {queuedItems.length} report(s) queued offline — will sync when online.
        </div>
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && reports.length === 0 && queuedItems.length === 0 && (
          <div className="text-center py-12 space-y-3">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No daily reports yet</p>
            <Button
              onClick={() =>
                navigate(`/work/daily-reports/${projectId}/new`)
              }
            >
              <Plus className="h-4 w-4 mr-1" /> Create First Report
            </Button>
          </div>
        )}

        {/* Queued (offline) items */}
        {queuedItems.map((q) => (
          <Card key={q.id} className="border-amber-200 bg-amber-50/50">
            <CardContent className="py-3 px-4">
              <div className="flex items-center gap-3">
                <CloudOff className="h-5 w-5 text-amber-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">Queued Report</p>
                  <p className="text-xs text-muted-foreground">
                    {formatDateOnly((q.payload as Record<string, string>).date)} — waiting to sync
                  </p>
                </div>
                <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                  Queued
                </Badge>
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Server reports */}
        {reports.map((report) => (
          <ReportCard
            key={report.id}
            report={report}
            projectId={projectId}
            timeZone={timeZone}
          />
        ))}
      </div>
    </div>
  );
}

function ReportCard({
  report,
  projectId,
  timeZone,
}: {
  report: DailyReport;
  projectId: number;
  timeZone: string;
}) {
  const navigate = useNavigate();

  const isSubmitted = !!report.submitted_at;
  const crewCount = report.crew_entries.length;
  const delayCount = report.delay_entries.length;

  return (
    <Card
      className="cursor-pointer hover:border-primary/50 transition-colors"
      onClick={() =>
        navigate(`/work/daily-reports/${projectId}/${report.id}`)
      }
    >
      <CardContent className="py-3 px-4">
        <div className="flex items-start gap-3">
          <div className="bg-primary/10 rounded-lg p-2 shrink-0">
            <Calendar className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium">{formatDateOnly(report.date)}</p>
              {isSubmitted ? (
                <Badge variant="secondary" className="text-xs">
                  <Cloud className="h-3 w-3 mr-0.5" /> Submitted
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                  Draft
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              by {report.reported_by_username}
              {report.weather && ` · ${report.weather}`}
              {report.submitted_at &&
                ` · Submitted ${formatDateTimeInTimeZone(report.submitted_at, timeZone)}`}
            </p>
            <div className="flex gap-3 mt-1.5 text-xs text-muted-foreground">
              {report.shift_start && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {report.shift_start}–{report.shift_end}
                </span>
              )}
              {crewCount > 0 && <span>{crewCount} crew</span>}
              {delayCount > 0 && (
                <span className="text-amber-600">{delayCount} delay(s)</span>
              )}
              {report.productive_hours && (
                <span>{report.productive_hours}h productive</span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
